import { Order, OrderIndexParams, OrderIndexParamsStatus } from '@rd/types';
import { OrderAction, OrderActionTypes } from '../actions/order.actions';

export interface OrderState {
  ordersList: {
    loading: boolean;
    error: boolean;
    orders: Order[];
    filters: {
      filter: {
        status: OrderIndexParamsStatus[];
        orderItemType: string[];
      };
      sort: OrderIndexParams['sort'];
    };
  };
  ordersMap: {
    loading: boolean;
    error: boolean;
    orders: Record<string, Order>;
    selected: string;
  };
}

const initialState: OrderState = {
  ordersList: {
    loading: false,
    error: false,
    orders: [],
    filters: {
      filter: {
        status: [],
        orderItemType: [],
      },
      sort: '-created_at',
    },
  },
  ordersMap: {
    loading: false,
    error: false,
    orders: {},
    selected: '',
  },
};

export function orderReducer(
  state: OrderState = initialState,
  action: OrderAction,
): OrderState {
  switch (action.type) {
    case OrderActionTypes.GET_ORDERS: {
      // GET_ORDERS can take in a payload with a filter and/or sort (ex: using the filter/sort dialog)
      // or it can NOT take in a payload (ex: changing points accounts).
      // If a payload with the keys were supplied, then hydrate that

      const filters = state.ordersList.filters;
      const sort = action.payload.sort ?? filters.sort;
      const { status, orderItemType } = action.payload.filter ?? filters.filter;

      return {
        ...state,
        ordersList: {
          ...state.ordersList,
          filters: {
            filter: {
              status,
              orderItemType,
            },
            sort,
          },
          orders: [],
          loading: true,
          error: false,
        },
      };
    }
    case OrderActionTypes.GET_ORDERS_SUCCESS: {
      return {
        ...state,
        ordersList: {
          ...state.ordersList,
          loading: false,
          error: false,
          orders: [...action.payload],
        },
      };
    }
    case OrderActionTypes.GET_ORDERS_FAIL: {
      return {
        ...state,
        ordersList: {
          ...state.ordersList,
          loading: false,
          error: true,
        },
      };
    }
    case OrderActionTypes.GET_ORDER: {
      // If we already have this in the store, we don't have to
      // set state here so that the UI doesn't have to load
      if (state.ordersMap.orders[action.payload]) {
        return {
          ...state,
          ordersMap: {
            ...state.ordersMap,
            selected: action.payload,
          },
        };
      }

      return {
        ...state,
        ordersMap: {
          ...state.ordersMap,
          loading: true,
          error: false,
          selected: action.payload,
        },
      };
    }
    case OrderActionTypes.GET_ORDER_SUCCESS: {
      const newOrdersMap = {
        ...state.ordersMap.orders,
        [action.payload.id]: action.payload,
      };
      return {
        ...state,
        ordersMap: {
          ...state.ordersMap,
          orders: newOrdersMap,
          loading: false,
          error: false,
        },
      };
    }
    case OrderActionTypes.GET_ORDER_FAIL: {
      return {
        ...state,
        ordersMap: {
          ...state.ordersMap,
          loading: false,
          error: true,
        },
      };
    }
    case OrderActionTypes.SELECT_ORDER: {
      return {
        ...state,
        ordersMap: {
          ...state.ordersMap,
          // @ts-ignore
          orders: {
            ...state.ordersMap.orders,
            [action.payload]: state.ordersList.orders.find(
              (order) => order.id === action.payload,
            ),
          },
          selected: action.payload,
        },
      };
    }
    case OrderActionTypes.UNSELECT_ORDER: {
      return {
        ...state,
        ordersMap: {
          ...state.ordersMap,
          selected: '',
        },
      };
    }
    case OrderActionTypes.RESET_ORDER_MAP: {
      return {
        ...state,
        ordersMap: {
          ...initialState.ordersMap,
        },
      };
    }
    default: {
      return state;
    }
  }
}
